import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SeoHead from '@/components/SeoHead';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

interface ServicePageProps {
  slug: string;
  title: string;
  titleAr: string;
  description: string;
  descriptionAr: string;
  keywords: string;
  heroImage: string;
  icon: string;
  features: { title: string; titleAr: string; description: string; descriptionAr: string }[];
  benefits: { title: string; titleAr: string; description: string; descriptionAr: string }[];
  faqs: { question: string; questionAr: string; answer: string; answerAr: string }[];
  relatedServices: { slug: string; title: string; titleAr: string; icon: string }[];
}

export default function ServicePage({
  slug,
  title,
  titleAr,
  description,
  descriptionAr,
  keywords,
  heroImage,
  icon,
  features,
  benefits,
  faqs,
  relatedServices,
}: ServicePageProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isAr = lang === 'ar';

  const displayTitle = isAr ? titleAr : title;
  const displayDescription = isAr ? descriptionAr : description;

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: displayTitle,
    description: displayDescription,
    provider: {
      '@type': 'Organization',
      name: 'Debug',
      url: 'https://debuging.com/',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'EG',
        addressLocality: 'Cairo',
      },
    },
    areaServed: {
      '@type': 'Country',
      name: 'Egypt',
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: displayTitle,
      itemListElement: features.map((f, i) => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: isAr ? f.titleAr : f.title,
          description: isAr ? f.descriptionAr : f.description,
        },
        position: i + 1,
      })),
    },
    serviceType: displayTitle,
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: isAr ? faq.questionAr : faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: isAr ? faq.answerAr : faq.answer,
      },
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: isAr ? 'الرئيسية' : 'Home',
        item: 'https://debuging.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: isAr ? 'الخدمات' : 'Services',
        item: 'https://debuging.com/#services',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: displayTitle,
        item: `https://debuging.com/${slug}`,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title={isAr ? `${titleAr} | Debug مصر` : `${title} | Debug Egypt`}
        description={displayDescription}
        keywords={keywords}
        canonical={`https://debuging.com/${slug}`}
        lang={lang}
      />
      <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <header>
        <Navbar />
      </header>

      <main className="pt-20">
        {/* Hero */}
        <section className="relative h-[420px] md:h-[520px] overflow-hidden">
          <img
            src={heroImage}
            alt={displayTitle}
            title={displayTitle}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60" />
          <div className="relative z-10 h-full flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <nav className="flex items-center gap-2 text-sm text-white/70 mb-6" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-white transition-colors">{t('home')}</Link>
                <i className={isAr ? 'ri-arrow-left-line text-xs' : 'ri-arrow-right-line text-xs'}></i>
                <Link to="/" className="hover:text-white transition-colors">{t('services')}</Link>
                <i className={isAr ? 'ri-arrow-left-line text-xs' : 'ri-arrow-right-line text-xs'}></i>
                <span className="text-white font-medium">{displayTitle}</span>
              </nav>
              <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center mb-6">
                <i className={`${icon} text-3xl text-white`}></i>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 leading-tight">
                {displayTitle}
              </h1>
              <p className="text-xl text-white/90 max-w-2xl leading-relaxed">
                {displayDescription}
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 bg-white" id="features">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {isAr ? 'ما نقدمه' : 'What We Offer'}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {isAr
                  ? 'حلول شاملة مصممة خصيصاً لتلبية احتياجات عملك'
                  : 'Comprehensive solutions tailored to meet your business needs'}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, idx) => (
                <div
                  key={idx}
                  className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-gray-100"
                >
                  <div className="w-12 h-12 bg-[rgb(0,0,139)] rounded-lg flex items-center justify-center mb-5">
                    <i className="ri-check-line text-xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {isAr ? feature.titleAr : feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {isAr ? feature.descriptionAr : feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 bg-gray-50" id="benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {isAr ? 'لماذا تختار Debug؟' : 'Why Choose Debug?'}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {isAr
                  ? 'الفوائد التي تحصل عليها عند العمل مع فريقنا'
                  : 'The benefits you get when working with our team'}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {benefits.map((benefit, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-2xl p-8 border border-gray-100 flex items-start gap-5"
                >
                  <div className="w-12 h-12 bg-[rgb(0,0,139)]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-star-line text-[rgb(0,0,139)] text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {isAr ? benefit.titleAr : benefit.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {isAr ? benefit.descriptionAr : benefit.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-white" id="faq">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {isAr ? 'الأسئلة الشائعة' : 'Frequently Asked Questions'}
              </h2>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div
                  key={idx}
                  className="bg-gray-50 rounded-xl p-6 border border-gray-100"
                  itemScope
                  itemType="https://schema.org/Question"
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-3" itemProp="name">
                    {isAr ? faq.questionAr : faq.question}
                  </h3>
                  <p className="text-gray-600 leading-relaxed" itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                    <span itemProp="text">{isAr ? faq.answerAr : faq.answer}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-[rgb(0,0,139)]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {isAr ? 'مستعد للبدء؟' : 'Ready to Get Started?'}
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              {isAr
                ? 'دعنا نناقش كيف يمكننا مساعدتك في تحقيق أهدافك.'
                : 'Let\'s discuss how we can help you achieve your goals.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                onClick={() => {
                  setTimeout(() => {
                    const el = document.getElementById('contact');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="inline-flex items-center justify-center bg-white text-[rgb(0,0,139)] px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
              >
                {isAr ? 'احصل على عرض أسعار' : 'Get a Quote'}
                <i className={isAr ? 'ri-arrow-left-line ml-2' : 'ri-arrow-right-line ml-2'}></i>
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
              >
                {isAr ? 'تعرف على المزيد' : 'Learn More'}
              </Link>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">
              {isAr ? 'خدمات ذات صلة' : 'Related Services'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedServices.map((rs) => (
                <Link
                  key={rs.slug}
                  to={`/${rs.slug}`}
                  className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-md transition-all hover:-translate-y-1 flex items-center gap-4 cursor-pointer"
                >
                  <div className="w-12 h-12 bg-[rgb(0,0,139)]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${rs.icon} text-[rgb(0,0,139)] text-xl`}></i>
                  </div>
                  <span className="font-bold text-gray-900">
                    {isAr ? rs.titleAr : rs.title}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer>
        <Footer />
      </footer>
    </div>
  );
}